import { Injectable } from '@angular/core';
import { ethers } from 'ethers';

import { eChainId } from '@common/networks';
import { environment } from '../../../../environments/environment';
import { NetworksService } from '../../networks/networks.service';
import
{
    eBlockLookup,
    ePageLookup,
    eRejectFailReason,
    IBlockChainExplorer,
    IGasStats,
    IOracleGas,
    IStoreExplorer,
    ITransactionResponceEx,
} from './types';

interface IPulseChainResponse<T>
{
    status: string;
    message: string;
    items: T;
}
interface IAvaxChainResponse<T>
{
    status: string;
    message: string;
    items: T;
}

interface IEVMResponce<T>
{
    status: string;
    message: string;

    result: T;
}

interface IEVMHistoryNodeResponce
{
    timeStamp: number;
    value: string;
    from: string;
    to: string;
    hash: string;
    input: string;
    confirmations: string;
    blockNumber: string;

    gas: string;
    gasPrice: string;
    gasUsed: string;
}

interface IEVMHistoryResponce extends IEVMResponce<IEVMHistoryNodeResponce[]>
{
}

interface IEVMABIResponce extends IEVMResponce<string>
{
}

interface IEVMBlockResponce extends IEVMResponce<string>
{
}

interface IEVMEstimatationResponce extends IEVMResponce<string>
{
}

interface IEVMGasOracleResponceResult
{
    SafeGasPrice: string,
    ProposeGasPrice: string,
    FastGasPrice: string,
}

interface IEVMGasOracleResponce extends IEVMResponce<IEVMGasOracleResponceResult>
{
}

enum eEVMModule
{
    PROXY = "proxy",
    ACCOUNT = "account",
    CONTRACT = "contract",
    GAS_TRACKER = "gastracker"
}

enum eEVMAction
{
    ETH_BLOCKNUMBER = "eth_blockNumber",
    GASORACLE = "gasoracle",
    GASESTIMATE = "gasestimate",
    TOKENTX = "tokentx",
    TXLIST = "txlist"
}

function ParseWei(_value: string)
{
    return ethers.parseUnits(_value, 'wei');
}

@Injectable({
    providedIn: 'root'
})
export class EVMBlockChainExplorerFactoryService
{
    protected explorers: IStoreExplorer[] = [];
    private chainId: number;
    constructor(private networkService: NetworksService)
    {
    }

    public async getExplorer(_chainId: number, _explorer?: string)
    {
        const findExplorer = this.takeExplorer(_chainId);

        if (findExplorer != undefined)
        {
            return findExplorer;
        }

        let explorer = _explorer;

        if (!explorer)
        {
            explorer = (await this.networkService.getBlockchainByChainId(_chainId)).explorer;
        }

        return await this.createExplorer(_chainId, explorer);
    }

    public async createExplorer(_chainId: number, _explorer: string)
    {
        const newExplorer = await this.createEVMExplorer(_explorer, _chainId);

        this.explorers.push({
            chainId: _chainId,
            EVMExplorer: newExplorer
        });

        return newExplorer;
    }

    private takeExplorer(_chainId: number): IBlockChainExplorer | undefined
    {
        if (this.explorers && this.explorers.length > 0)
        {
            const findExplorer = this.explorers.find(v => v.chainId === _chainId);

            if (findExplorer != undefined)
            {
                return findExplorer.EVMExplorer;
            }
        }
        return undefined;
    }

    private async createEVMExplorer(_explorer: string, _chainId: number): Promise<IBlockChainExplorer>
    {
        const pulseChain = [ eChainId.PULSECHAIN_TESTNET, eChainId.PULSECHAIN_MAINNET ];
        const avaxChain = [ eChainId.AVALACHE_MAINNET, eChainId.AVALACHE_TESTNET ];
        // const bscChain = [ eChainId.BSC_MAINNET, eChainId.BSC_TESTNET ];


        // if (bscChain.includes(_chainId))
        // {
        //     const mainnetExplorer = (await this.networkService.getMainnetByChainId(_chainId)).explorer;
        //     return new eEVMBlockChainExplorer(_explorer, _chainId, mainnetExplorer);
        // }

        if (avaxChain.includes(_chainId))
        {
            const mainnetExplorer = (await this.networkService.getMainnetByChainId(_chainId)).explorer;
            return new AvaxChainExplorer(_explorer, _chainId, mainnetExplorer);
        }

        if (pulseChain.includes(_chainId))
        {
            const mainnetExplorer = (await this.networkService.getMainnetByChainId(_chainId)).explorer;
            return new PulseChainExplorer(_explorer, _chainId, mainnetExplorer);
        }

        const mainnetExplorer = (await this.networkService.getMainnetByChainId(_chainId)).explorer;
        return new eEVMBlockChainExplorer(_explorer, _chainId, mainnetExplorer);
    }
}

export class PulseChainExplorer implements IBlockChainExplorer
{
    private api: string;
    private apiKey: string;
    private mainnetApi: string;
    private chainId: number;

    constructor(_api: string, _chainId: number, _mainnetApi: string)
    {
        this.api = _api;
        this.apiKey = environment[ _chainId ].ExplorerApi;
        this.mainnetApi = _mainnetApi;
        this.chainId = _chainId;
    }

    public async GetEstimatationOfConfirmTime(gasPrice: string): Promise<number>
    {
        throw new Error("Method not implemented.");
    }

    public async GetBlockNumber(): Promise<number>
    {
        const endpoint = `/stats`;
        const statsRes = await this.Fetch(endpoint);
        return Number(statsRes.total_blocks);
    }

    public async GetHistory(
        _address: string,
        _contractAddress: string,
        _lookSettings: ePageLookup | eBlockLookup): Promise<ITransactionResponceEx[]>
    {
        const endpoint = _contractAddress
            ? ``
            : `/addresses/${ _address }/transactions?filters=${ encodeURIComponent('to | from') }`;

        const data = await this.Fetch(endpoint);

        if (!data?.items?.length) return [];

        const items = data?.items.map((tx) =>
        {
            return {
                from: tx.from.hash,
                to: tx.to.hash,
                hash: tx.hash,
                data: tx.raw_input,

                gasLimit: tx.gas_limit,
                gasUsed: tx.gas_used,
                gasPrice: tx.max_fee_per_gas,

                timestamp: new Date(tx.timestamp).getTime() / 1000,
                confirmations: tx.confirmations,
                value: tx.value,
                blockNumber: tx.block,
                nonce: tx.nonce,
                state: tx.status === 'ok' ? 0 : 1,
            };
        });
        return items;
    }

    public async GetContractABI(_address: string): Promise<string>
    {
        let url = `/smart-contracts/${ _address }`;
        return (<any>await this.Fetch(url)).abi;
    }

    public async GetGasOracle(): Promise<IOracleGas>
    {
        const endpoint = `/stats`;
        const statsRes = await this.Fetch(endpoint);
        const gasStats: IGasStats = statsRes.gas_prices;
        const suggestedBaseFee = ((gasStats.average + gasStats.fast + gasStats.slow) / 3) * 0.8875;

        try
        {
            return {
                safeGasPrice:
                {
                    price: String(gasStats.slow),
                    priorityAmount: Math.ceil(Number(gasStats.slow) - Number(suggestedBaseFee)) ?
                        Math.ceil(Number(gasStats.slow) - Number(suggestedBaseFee)).toString() :
                        '1'
                },
                proposeGasPrice:
                {
                    price: String(gasStats.average),
                    priorityAmount: Math.ceil(Number(gasStats.average) - Number(suggestedBaseFee)) ?
                        Math.ceil(Number(gasStats.average) - Number(suggestedBaseFee)).toString() :
                        '5'
                },
                fastGasPrice:
                {
                    price: String(gasStats.fast),
                    priorityAmount: Math.ceil(Number(gasStats.fast) - Number(suggestedBaseFee)) ?
                        Math.ceil(Number(gasStats.fast) - Number(suggestedBaseFee)).toString() :
                        '10'
                },
            };
        }
        catch (e)
        {
            return {
                safeGasPrice:
                {
                    price: '1',
                    priorityAmount: '1'
                },
                proposeGasPrice:
                {
                    price: '10',
                    priorityAmount: '10'
                },
                fastGasPrice:
                {
                    price: '15',
                    priorityAmount: '15'
                },
            };
        }
    }

    private async Fetch(endpoint: string): Promise<any>
    {
        let url = `${ this.api }${ endpoint }`;

        return fetch(url).then(async res =>
        {
            var data = <IPulseChainResponse<any>>await res.json();
            this.ParseFailReason(data);
            return data;
        }).catch(e =>
        {
            console.error(e + ' ' + endpoint);
        });
    }

    private ParseFailReason<T>(_responce: IPulseChainResponse<T>)
    {
        //todo check http return code

        const S_OK = "1";

        // NOTE: Etherscan for empty history returns next:
        // >>>Status: "0", message: "No transactions found", result: Array(0)}
        // Here "0" is rather normal status, so if _responce.result is string,
        // then it must be error message. Or no??

        //todo use npm styring check
        if (_responce.status !== S_OK && typeof ((<any>_responce).result) === "string")
        {
            if ((<any>_responce).result.toLowerCase() === 'max rate limit reached')
            {
                throw eRejectFailReason.FAIL_BY_LIMIT_REACHED;
            }
            else
            {
                throw eRejectFailReason.FAIL;
            }
        }
    }
}

export class eEVMBlockChainExplorer implements IBlockChainExplorer
{
    private api: string;
    private apiKey: string;
    private mainnetApi: string;
    private chainId: number;

    constructor(_api: string, _chainId: number, _mainnetApi: string)
    {
        this.api = _api;
        this.apiKey = environment[ _chainId ].ExplorerApi;
        this.mainnetApi = _mainnetApi;
        this.chainId = _chainId;
    }

    //#region Public Methods
    public async GetEstimatationOfConfirmTime(gasPrice: string): Promise<number>
    {
        let action = `${ eEVMAction.GASESTIMATE }&gasprice=${ gasPrice }`;
        let res = (<IEVMEstimatationResponce>await this.Fetch(eEVMModule.GAS_TRACKER, action));
        return Number(res.result);
    }

    public async GetBlockNumber(): Promise<number>
    {
        let res = (<IEVMBlockResponce>await this.Fetch(eEVMModule.PROXY, eEVMAction.ETH_BLOCKNUMBER));
        return Number(res.result);
    }

    public async GetHistory(
        _address: string,
        _contractAddress: string,
        _lookSettings: ePageLookup | eBlockLookup): Promise<ITransactionResponceEx[]>
    {

        let action = _contractAddress != null ? `${ eEVMAction.TOKENTX }&contractaddress=${ _contractAddress }`
            : eEVMAction.TXLIST;

        let url = `${ action }`
            + `&address=${ _address }`
            + `&sort=desc`;

        if (_lookSettings instanceof ePageLookup)
        {
            url += `&page=${ _lookSettings.page }&offset=${ _lookSettings.page }`;
        }
        else
        {
            url += `&startblock=${ _lookSettings.startBlock }&endblock=${ _lookSettings.endBlock }`;
        }

        let res = <IEVMHistoryResponce>await this.Fetch(eEVMModule.ACCOUNT, url);

        let history = res.result.map((_tx) =>
        {
            return {
                from: _tx.from,
                to: _tx.to,
                hash: _tx.hash,
                data: _tx.input,

                gasLimit: ParseWei(_tx.gas),
                gasUsed: ParseWei(_tx.gasUsed),
                gasPrice: ParseWei(_tx.gasPrice),

                timestamp: Number(_tx.timeStamp),
                confirmations: Number(_tx.confirmations),
                value: ParseWei(_tx.value),
                blockNumber: Number(_tx.blockNumber),
            };
        });

        return history;
    }

    public async GetContractABI(_address: string): Promise<string>
    {
        let url = `getabi&address=${ _address }`;
        return (<IEVMABIResponce>await this.Fetch(eEVMModule.CONTRACT, url)).result;
    }

    public async GetGasOracle(): Promise<IOracleGas>
    {
        let response = (await this.Fetch(eEVMModule.GAS_TRACKER, eEVMAction.GASORACLE));

        try
        {
            return {
                safeGasPrice:
                {
                    price: response.result.SafeGasPrice,
                    priorityAmount: Math.ceil(Number(response.result.SafeGasPrice) - Number(response.result.suggestBaseFee)) ?
                        Math.ceil(Number(response.result.SafeGasPrice) - Number(response.result.suggestBaseFee)).toString() :
                        '1'
                },
                proposeGasPrice:
                {
                    price: response.result.ProposeGasPrice,
                    priorityAmount: Math.ceil(Number(response.result.ProposeGasPrice) - Number(response.result.suggestBaseFee)) ?
                        Math.ceil(Number(response.result.ProposeGasPrice) - Number(response.result.suggestBaseFee)).toString() :
                        '5'
                },
                fastGasPrice:
                {
                    price: response.result.FastGasPrice,
                    priorityAmount: Math.ceil(Number(response.result.FastGasPrice) - Number(response.result.suggestBaseFee)) ?
                        Math.ceil(Number(response.result.FastGasPrice) - Number(response.result.suggestBaseFee)).toString() :
                        '10'
                },
            };
        } catch
        {
            return {
                safeGasPrice:
                {
                    price: '1',
                    priorityAmount: '1'
                },
                proposeGasPrice:
                {
                    price: '10',
                    priorityAmount: '10'
                },
                fastGasPrice:
                {
                    price: '15',
                    priorityAmount: '15'
                },
            };
        }

        // return {
        //     proposeGasPrice: apiRes.ProposeGasPrice,
        //     fastGasPrice:    apiRes.FastGasPrice,
        //     safeGasPrice:    apiRes.SafeGasPrice,
        // }
    }
    //#endregion Public Methods

    private ParseFailReason<T>(_responce: IEVMResponce<T>)
    {
        //todo check http return code

        const S_OK = "1";

        // NOTE: Etherscan for empty history returns next:
        // >>>Status: "0", message: "No transactions found", result: Array(0)}
        // Here "0" is rather normal status, so if _responce.result is string,
        // then it must be error message. Or no??

        //todo use npm styring check
        if (_responce.status !== S_OK && typeof ((<any>_responce).result) === "string")
        {
            if ((<any>_responce).result.toLowerCase() === 'max rate limit reached')
            {
                throw eRejectFailReason.FAIL_BY_LIMIT_REACHED;
            }
            else
            {
                throw eRejectFailReason.FAIL;
            }
        }
    }

    private async Fetch(_module: string, _action: string): Promise<any>
    {
        let urlObj = new URL(this.api);
        urlObj.searchParams.set('module', _module);
        
        let url = `${ urlObj.toString() }&action=${ _action }`;

        if (_action === eEVMAction.GASORACLE && (this.chainId === 80002))
        {
            let urlObj = new URL(this.mainnetApi);
            urlObj.searchParams.set('module', _module); 
            url = `${ urlObj.toString() }&action=${ _action }`;
        }

        if (this.apiKey)
        {
            // urlObj.searchParams.set('apikey', this.apiKey);
            url += `&apikey=${ this.apiKey }`;
        }

        return fetch(url).then(async res =>
        {
            var data = <IEVMResponce<any>>await res.json();
            this.ParseFailReason(data);
            return data;
        }).catch(e =>
        {
            console.error(e + ' ' + _action);
        });
    }
}

export class AvaxChainExplorer implements IBlockChainExplorer
{
    private api: string;
    private apiKey: string;
    private mainnetApi: string;
    private chainId: number;

    constructor(_api: string, _chainId: number, _mainnetApi: string)
    {
        this.api = _api;
        this.apiKey = environment[ _chainId ].ExplorerApi;
        this.mainnetApi = _mainnetApi;
        this.chainId = _chainId;
    }

    public async GetBlockNumber(): Promise<number>
    {
        let res = (<any>await this.Fetch(eEVMModule.PROXY, eEVMAction.ETH_BLOCKNUMBER));
        return Number(res.result);
    }

    public async GetHistory(_address: string, _contractAddress: string, _lookSettings: ePageLookup | eBlockLookup): Promise<ITransactionResponceEx[]>
    {
        let url = `${ eEVMAction.TXLIST }`
            + `&address=${ _address }`
            + `&sort=desc`;


        if (_lookSettings instanceof ePageLookup)
        {
            url += `&page=${ _lookSettings.page }&offset=${ _lookSettings.page }`;
        }
        else
        {
            url += `&startblock=${ _lookSettings.startBlock }&endblock=${ _lookSettings.endBlock }`;
        }


        let res = <any>await this.Fetch(eEVMModule.ACCOUNT, url);

        let history = res.result.map((_tx) =>
        {
            return {
                from: _tx.from,
                to: _tx.to,
                hash: _tx.hash,
                data: _tx.input,

                gasLimit: ParseWei(_tx.gas),
                gasUsed: ParseWei(_tx.gasUsed),
                gasPrice: ParseWei(_tx.gasPrice),

                timestamp: Number(_tx.timeStamp),
                confirmations: Number(_tx.confirmations),
                value: ParseWei(_tx.value),
                blockNumber: Number(_tx.blockNumber),
            };
        });

        return history;
    }
    public async GetGasOracle(): Promise<any>
    {
        let res = (await this.Fetch('proxy', 'eth_gasPrice'));

        try
        {
            return {
                safeGasPrice:
                {
                    price: String(Number(res.result) * 0.5).substring(0, 2),
                    priorityAmount: '1'
                },
                proposeGasPrice:
                {
                    price: String(Number(res.result)).substring(0, 2),
                    priorityAmount: '5'
                },
                fastGasPrice:
                {
                    price: String(Number(res.result) * 2).substring(0, 2),
                    priorityAmount: '10'
                },
            };
        } catch
        {
            return {
                safeGasPrice:
                {
                    price: '1',
                    priorityAmount: '1'
                },
                proposeGasPrice:
                {
                    price: '10',
                    priorityAmount: '10'
                },
                fastGasPrice:
                {
                    price: '15',
                    priorityAmount: '15'
                },
            };
        }
    }
    public async GetEstimatationOfConfirmTime(gasPrice: string): Promise<number>
    {
        throw new Error('Method not implemented.');
    }
    public async GetContractABI(_address: string): Promise<string>
    {
        let url = `getabi&address=${ _address }`;
        return (<any>await this.Fetch(eEVMModule.CONTRACT, url)).result;
    }

    private ParseFailReason<T>(_responce: IAvaxChainResponse<T>)
    {
        //todo check http return code

        const S_OK = "1";

        // NOTE: Etherscan for empty history returns next:
        // >>>Status: "0", message: "No transactions found", result: Array(0)}
        // Here "0" is rather normal status, so if _responce.result is string,
        // then it must be error message. Or no??

        //todo use npm styring check
        if (_responce.status !== S_OK && typeof ((<any>_responce).result) === "string")
        {
            if ((<any>_responce).result.toLowerCase() === 'max rate limit reached')
            {
                throw eRejectFailReason.FAIL_BY_LIMIT_REACHED;
            }
            else
            {
                throw eRejectFailReason.FAIL;
            }
        }
    }

    private async Fetch(_module: string, _action: string): Promise<any>
    {
        let url = `${ this.api }?module=${ _module }&action=${ _action }`;

        if (this.apiKey)
        {
            url += `&apikey=${ this.apiKey }`;
        }

        return fetch(url).then(async res =>
        {
            let data = <IAvaxChainResponse<any>>await res.json();
            // this.ParseFailReason(data);
            return data;
        }).catch(e =>
        {
            console.error(e + ' ' + _action);
        });
    }
}
